// frontend/src/hooks/useTimeSeriesFetcher.js
//
// Shared fetcher for time-series chart endpoints (inventory timeline,
// demand timeline, sales progress, etc.). Wraps a `useQuery` call with:
//   - URL building (params → searchParams)
//   - Standard read result (`readResult`: translated error, no data beside it)
//   - Per-resource staleTime override (defaults to 2 min for charts)
//
// Keeps the per-domain hooks (useInventoryTimeline, useDemandTimeline,
// useSalesProgress) thin: they assemble params + a queryKey, this hook
// does the rest.

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { sessionApi } from '../services/SessionApiService';
import { apiErrorFrom } from '../utils/parseApiError';
import { readResult } from '../queries/api';

const TWO_MIN = 2 * 60 * 1000;

/**
 * @param {object} args
 * @param {Array}  args.queryKey  — full TanStack key (built by caller via queries/keys.js)
 * @param {string} args.endpoint  — REST endpoint, no query string
 * @param {object} args.params    — flat dict, serialised via URLSearchParams
 * @param {boolean} [args.enabled=true] — gate the fetch (e.g. on storeId resolve)
 * @param {number}  [args.staleTime=TWO_MIN] — per-resource override
 */
export function useTimeSeriesFetcher({
  queryKey,
  endpoint,
  params,
  enabled = true,
  staleTime = TWO_MIN,
}) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const search = new URLSearchParams(params);
      const res = await sessionApi.get(`${endpoint}?${search}`);
      if (!res.ok) throw await apiErrorFrom(res);
      return res.json();
    },
    staleTime,
    enabled,
  });
  return readResult(query, t);
}
